import React, { useMemo } from 'react';

/**
 * CeleyDelightLayer — Unified animation system for the Celey customer experience.
 *
 * Modes:
 *   "idle"        → Subtle ambient motion. Use on empty states.
 *   "momentum"    → Slightly more active. Use on returning user hero.
 *   "celebration" → High energy. Use on confirmation screens, milestones.
 *
 * Mood (optional, applies to "celebration" mode only):
 *   "elegant"     → Wedding/engagement: refined shimmer, slow gold/ivory, fewer particles.
 *   "warm"        → Anniversary: soft warm pulse, intimate, rose/gold tones.
 *   "corporate"   → Minimal, nearly static, muted neutral dots.
 *   "celebration" → Birthday/default: lively, colorful, full energy (unchanged).
 *
 * Usage:
 *   <CeleyDelightLayer mode="celebration" mood="elegant" />
 */

// ─── Base mode configs (non-celebration) ───
const BASE_MODES = {
  idle: {
    particles: [
      { size: 5, x: '18%', y: '22%', color: '#C6A96B', delay: 0,   dur: 7, opacity: 0.18 },
      { size: 4, x: '78%', y: '35%', color: '#D4B87A', delay: 2.5, dur: 8, opacity: 0.14 },
      { size: 3, x: '45%', y: '72%', color: '#C6A96B', delay: 4,   dur: 9, opacity: 0.12 },
    ],
    shimmer: true,
    shimmerInterval: 7,
    shimmerColor: 'rgba(198,169,107,0.06)',
    animation: 'celeyFloat',
  },
  momentum: {
    particles: [
      { size: 5, x: '12%', y: '20%', color: '#C6A96B', delay: 0,   dur: 5,   opacity: 0.22 },
      { size: 4, x: '82%', y: '30%', color: '#D4B87A', delay: 1.5, dur: 5.5, opacity: 0.20 },
      { size: 6, x: '55%', y: '65%', color: '#C6A96B', delay: 3,   dur: 6,   opacity: 0.18 },
      { size: 3, x: '30%', y: '80%', color: '#D4B87A', delay: 0.8, dur: 6.5, opacity: 0.16 },
      { size: 4, x: '68%', y: '15%', color: '#C6A96B', delay: 2.2, dur: 5,   opacity: 0.15 },
    ],
    shimmer: true,
    shimmerInterval: 5,
    shimmerColor: 'rgba(198,169,107,0.06)',
    animation: 'celeyFloat',
  },
};

// ─── Celebration mood variants ───
const CELEBRATION_MOODS = {
  // Wedding / Engagement — refined shimmer, slow gold/ivory, fewer particles
  elegant: {
    particles: [
      { size: 4, x: '15%', y: '18%', color: '#D4C8A0', delay: 0,   dur: 5,   opacity: 0.25 },
      { size: 3, x: '80%', y: '25%', color: '#C6A96B', delay: 1.2, dur: 5.5, opacity: 0.22 },
      { size: 5, x: '50%', y: '12%', color: '#E8DCC0', delay: 0.8, dur: 6,   opacity: 0.28 },
      { size: 3, x: '30%', y: '78%', color: '#C6A96B', delay: 2.0, dur: 5.8, opacity: 0.20 },
      { size: 4, x: '70%', y: '70%', color: '#D4C8A0', delay: 1.5, dur: 5.2, opacity: 0.22 },
    ],
    shimmer: true,
    shimmerInterval: 4,
    shimmerColor: 'rgba(212,200,160,0.10)',
    animation: 'celeyFloat',
  },
  // Anniversary — warm pulse, intimate, rose/gold
  warm: {
    particles: [
      { size: 5, x: '20%', y: '20%', color: '#D4A890', delay: 0,   dur: 6,   opacity: 0.22 },
      { size: 4, x: '75%', y: '30%', color: '#C6A96B', delay: 1.8, dur: 6.5, opacity: 0.20 },
      { size: 6, x: '45%', y: '15%', color: '#D4A890', delay: 1.0, dur: 7,   opacity: 0.25 },
      { size: 4, x: '60%', y: '75%', color: '#C6A96B', delay: 2.5, dur: 6.2, opacity: 0.18 },
    ],
    shimmer: false,
    shimmerInterval: 0,
    shimmerColor: '',
    animation: 'celeyFloat',
  },
  // Corporate — minimal, nearly static, muted
  corporate: {
    particles: [
      { size: 3, x: '22%', y: '25%', color: '#B0B0A8', delay: 0,   dur: 8,   opacity: 0.14 },
      { size: 3, x: '78%', y: '35%', color: '#A0A098', delay: 3.0, dur: 8.5, opacity: 0.12 },
    ],
    shimmer: false,
    shimmerInterval: 0,
    shimmerColor: '',
    animation: 'celeyFloat',
  },
  // Birthday / default celebration — full energy (unchanged from original)
  celebration: {
    particles: [
      { size: 6, x: '10%', y: '15%', color: '#C6A96B', delay: 0,   dur: 2.5, opacity: 0.45 },
      { size: 5, x: '85%', y: '20%', color: '#10B981', delay: 0.3, dur: 2.8, opacity: 0.40 },
      { size: 7, x: '50%', y: '10%', color: '#FBBF24', delay: 0.6, dur: 3,   opacity: 0.50 },
      { size: 4, x: '25%', y: '75%', color: '#10B981', delay: 0.2, dur: 2.6, opacity: 0.38 },
      { size: 5, x: '72%', y: '80%', color: '#C6A96B', delay: 0.5, dur: 2.4, opacity: 0.42 },
      { size: 3, x: '40%', y: '90%', color: '#FBBF24', delay: 0.8, dur: 3.2, opacity: 0.35 },
      { size: 6, x: '15%', y: '50%', color: '#10B981', delay: 1.0, dur: 2.2, opacity: 0.40 },
      { size: 4, x: '88%', y: '55%', color: '#C6A96B', delay: 0.4, dur: 2.7, opacity: 0.38 },
      { size: 5, x: '60%', y: '40%', color: '#FBBF24', delay: 0.7, dur: 2.5, opacity: 0.32 },
      { size: 3, x: '35%', y: '25%', color: '#10B981', delay: 1.2, dur: 3.0, opacity: 0.30 },
    ],
    shimmer: false,
    shimmerInterval: 0,
    shimmerColor: '',
    animation: 'celeyCelebrate',
  },
};

const KEYFRAMES = `
@keyframes celeyFloat {
  0%, 100% { transform: translateY(0) scale(1); opacity: var(--p-opacity); }
  50% { transform: translateY(-12px) scale(1.15); opacity: calc(var(--p-opacity) * 1.4); }
}
@keyframes celeyShimmer {
  0%, 85%, 100% { opacity: 0; }
  90%, 95% { opacity: 0.35; }
}
@keyframes celeyCelebrate {
  0% { transform: translateY(0) scale(1); opacity: var(--p-opacity); }
  25% { transform: translateY(-18px) scale(1.3); opacity: calc(var(--p-opacity) * 1.6); }
  50% { transform: translateY(-8px) scale(0.9); opacity: var(--p-opacity); }
  75% { transform: translateY(-22px) scale(1.2); opacity: calc(var(--p-opacity) * 1.5); }
  100% { transform: translateY(0) scale(1); opacity: var(--p-opacity); }
}
`;

export const CeleyDelightLayer = ({ mode = 'idle', mood = '', className = '' }) => {
  const config = useMemo(() => {
    if (mode === 'celebration') {
      // Mood-aware celebration: elegant, warm, corporate, or default (birthday)
      return CELEBRATION_MOODS[mood] || CELEBRATION_MOODS.celebration;
    }
    return BASE_MODES[mode] || BASE_MODES.idle;
  }, [mode, mood]);

  const particles = config.particles;
  const animationName = config.animation;

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none z-[1] ${className}`}
      aria-hidden="true"
      data-testid={`delight-layer-${mode}${mood ? `-${mood}` : ''}`}
    >
      <style>{KEYFRAMES}</style>

      {particles.map((p, i) => (
        <div
          key={`${mode}-${mood}-p-${i}`}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            left: p.x,
            top: p.y,
            backgroundColor: p.color,
            '--p-opacity': p.opacity,
            opacity: p.opacity,
            animation: `${animationName} ${p.dur}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}

      {config.shimmer && (
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(105deg, transparent 40%, ${config.shimmerColor} 50%, transparent 60%)`,
            animation: `celeyShimmer ${config.shimmerInterval}s ease-in-out infinite`,
          }}
        />
      )}
    </div>
  );
};

export default CeleyDelightLayer;
